import { PROFILE_CATEGORY_META } from '@shared/constants/profile-categories';
import type { ExitsInvestmentsToCompleteProfile } from '@shared/schemas';

import { paths } from '@/shared/routes/paths';

import type {
  BrokenUrlProfile,
  CleaningRow,
  CleaningRowType,
  CleaningTypeFilter,
  DeleteBrokenUrlsResult,
  DuplicateGroup,
  DuplicateProfileEntry,
  MergeGroupResult,
  MixedCurrencyExitProfile,
} from './profile-cleaning.model';
import {
  CARD_ONLY_ROW_TYPES,
  CLEANING_ROW_TYPES,
  REVIEW_ONLY_ROW_TYPES,
} from './profile-cleaning.model';

/**
 * Pure derivations + message builders for the Profile Cleaning page. Everything
 * the original component computed inline (unified rows, counts, the clean /
 * merge snackbar strings) lives here as framework-free functions, so each rule is
 * unit-testable and the saga/views just call them.
 */

/** `/in/<slug>` of a LinkedIn URL; the part a reader compares. */
function linkedinPath(url: string): string {
  return url.replace(/^https?:\/\/[^/]+/, '');
}

/** "Exit amounts in EUR, USD, no currency" (#709): what an administrator converts by hand. */
export function mixedCurrenciesReason(currencies: (string | null)[]): string {
  return `Exit amounts in ${currencies.map((c) => c ?? 'no currency').join(', ')}`;
}

/**
 * "Business Angels; Exit entrepreneurs" (#754): the categories that put a
 * profile in the "Exits & investments to complete" queue, as the HNWIs band
 * names them.
 */
export function toCompleteReason(
  categories: ExitsInvestmentsToCompleteProfile['categories'],
): string {
  return categories.map((c) => PROFILE_CATEGORY_META[c].plural).join('; ');
}

/** Whether a row can be ticked: never a locked one (#702), never a review-only kind (#709). */
export function isCleaningRowSelectable(row: CleaningRow): boolean {
  return !row.is_cleaning_locked && !REVIEW_ONLY_ROW_TYPES.includes(row.row_type);
}

/** Kinds fixed on the profile's Exits & investments tab (#709, #754). */
const CAPITAL_TAB_ROW_TYPES: readonly CleaningRowType[] = [
  'mixed_exit_currencies',
  'exits_investments_to_complete',
];

/** Whether a row leads to the profile's Exits & investments tab, its name a link there. */
export function opensCapitalTab(row: Pick<CleaningRow, 'row_type'>): boolean {
  return CAPITAL_TAB_ROW_TYPES.includes(row.row_type);
}

/**
 * Where a row leads (#709). A mixed-currency row is fixed on the profile's
 * exits, and a profile to complete (#754) on its exits and investments, so both
 * open the Exits & investments tab. The whole row is the link: a link of its
 * own inside the row would be cancelled by the row's.
 */
export function cleaningRowHref(row: Pick<CleaningRow, 'id' | 'row_type'>): string {
  const href = paths.admin.profiles.detail(row.id);
  return opensCapitalTab(row) ? `${href}?tab=capital` : href;
}

/**
 * Flatten broken-URL profiles, duplicate groups, mixed-currency exits and the
 * profiles to complete (#754) into the unified table rows.
 */
export function buildCleaningRows(
  brokenUrlProfiles: BrokenUrlProfile[],
  duplicateGroups: DuplicateGroup[],
  mixedCurrencyProfiles: MixedCurrencyExitProfile[] = [],
  toCompleteProfiles: ExitsInvestmentsToCompleteProfile[] = [],
): CleaningRow[] {
  return [
    ...brokenUrlProfiles.map((p) => ({
      id: p.id,
      full_name: p.full_name,
      linkedin_url: p.linkedin_url,
      row_type: 'broken_url' as const,
      reason: p.status === 'not_found' ? 'Not Found' : p.status,
      date: p.updated_at,
      is_cleaning_locked: p.is_cleaning_locked,
    })),
    ...duplicateGroups.flatMap((group) =>
      group.profiles.map((p) => ({
        id: p.id,
        full_name: p.full_name,
        linkedin_url: p.linkedin_url,
        row_type: 'duplicate' as const,
        reason: `Same LinkedIn profile: ${linkedinPath(group.linkedin_url)}`,
        date: p.data_updated_at ?? '',
        is_cleaning_locked: p.is_cleaning_locked,
        group_key: group.linkedin_url,
        is_canonical: p.is_canonical,
        canonical_id: group.canonical_id,
        duplicate_ids: group.profiles.filter(isMergeable).map((pp) => pp.id),
      })),
    ),
    ...mixedCurrencyProfiles.map((p) => ({
      id: p.id,
      full_name: p.full_name,
      linkedin_url: p.linkedin_url,
      row_type: 'mixed_exit_currencies' as const,
      reason: mixedCurrenciesReason(p.currencies),
      date: p.updated_at,
      is_cleaning_locked: p.is_cleaning_locked,
    })),
    ...toCompleteProfiles.map((p) => ({
      id: p.id,
      full_name: p.full_name,
      linkedin_url: p.linkedin_url,
      row_type: 'exits_investments_to_complete' as const,
      reason: toCompleteReason(p.categories),
      // The last LinkedIn refresh; '' reads "-" in the Date column.
      date: p.data_updated_at ?? '',
      // Never cleaned, so never locked: the padlock column stays empty on it.
      is_cleaning_locked: false,
    })),
  ];
}

/**
 * A group member a merge deletes: not the canonical, and not locked from
 * cleaning (#702). The backend ranks a locked member canonical, so a second
 * locked one in the same group is the only locked duplicate there can be.
 */
function isMergeable(p: DuplicateProfileEntry): boolean {
  return !p.is_canonical && !p.is_cleaning_locked;
}

/** Mergeable duplicate count across all groups (the number that will be merged). */
export function duplicateProfileCount(groups: DuplicateGroup[]): number {
  return groups.reduce((acc, g) => acc + g.profiles.filter(isMergeable).length, 0);
}

/** A group with at least one duplicate left to merge. */
function hasMergeable(group: DuplicateGroup): boolean {
  return group.profiles.some(isMergeable);
}

// ── Type filter (#431) ──

/** Whether a row is one of "All issues": every kind but the card-only ones (#754). */
function isInAllIssues(row: CleaningRow): boolean {
  return !CARD_ONLY_ROW_TYPES.includes(row.row_type);
}

/**
 * The rows of one type, or "All issues": every row but the card-only kinds
 * (#754), shown only under their own card. `'all'` keeps the array identity
 * when nothing is left out.
 */
export function filterRowsByType(rows: CleaningRow[], filter: CleaningTypeFilter): CleaningRow[] {
  if (filter === 'all') {
    const issues = rows.filter(isInAllIssues);
    return issues.length === rows.length ? rows : issues;
  }
  return rows.filter((row) => row.row_type === filter);
}

/**
 * How many rows each filter segment stands for. `all` is what `'all'` lists:
 * the card-only kinds (#754) have their own count and are not in it, so it
 * keeps agreeing with the sidebar badge (#753).
 */
export function countRowsByType(rows: CleaningRow[]): Record<CleaningTypeFilter, number> {
  const counts = { all: rows.filter(isInAllIssues).length } as Record<CleaningTypeFilter, number>;
  for (const type of CLEANING_ROW_TYPES) counts[type] = 0;
  for (const row of rows) counts[row.row_type] += 1;
  return counts;
}

/**
 * Whether a scope is a card-only kind (#754). An empty table under it is that
 * queue done, not a filter excluding everything: no "No match", no "Clear filters".
 */
export function isCardOnlyScope(filter: CleaningTypeFilter): boolean {
  return filter !== 'all' && CARD_ONLY_ROW_TYPES.includes(filter);
}

/**
 * Whether the page says "All clear!" instead of the table. It is about the rows
 * to clean: nothing listed at all (today's case, the cards are gone too), or
 * only profiles to complete left under the default scope, whose card stays to
 * count them (#754). Any other scope keeps the table and its own empty state,
 * and a card-only scope whose list failed to load (`cardOnlyFailed`) keeps the
 * table to say so: an unread list is not a clear one.
 */
export function isAllClear(
  rows: CleaningRow[],
  filter: CleaningTypeFilter,
  cardOnlyFailed = false,
): boolean {
  if (cardOnlyFailed && isCardOnlyScope(filter)) return false;
  return rows.length === 0 || (filter === 'all' && filterRowsByType(rows, 'all').length === 0);
}

/**
 * Whether the cards are drawn. Not over nothing at all (zeroes and a filter
 * over an empty page say less than no tiles), but always when a card-only list
 * failed to load (#754): its card is where the page says so.
 */
export function showsCards(rows: CleaningRow[], cardOnlyFailed: boolean): boolean {
  return rows.length > 0 || cardOnlyFailed;
}

/**
 * The Type filter carried by the page's URL (`?type=`, #754), so going back
 * from a profile lands on the same card. Anything that is not a row type,
 * absent included, is "All issues".
 */
export function parseCleaningTypeFilter(raw: string | null): CleaningTypeFilter {
  return (CLEANING_ROW_TYPES as readonly string[]).includes(raw ?? '')
    ? (raw as CleaningTypeFilter)
    : 'all';
}

/** What a "Clean all" under the current filter would delete or merge. */
export interface CleanScopeCounts {
  /** Profiles that will actually be removed; the number the button and the dialog title carry. */
  rowCount: number;
  brokenUrlCount: number;
  dupeGroupCount: number;
  dupeProfileCount: number;
}

/**
 * The clean's reach under the active type filter: what is on screen is what gets
 * cleaned, so a scoped filter zeroes the other type's counts and the run skips
 * that type's calls.
 *
 * `rowCount` counts the profiles that actually disappear, in every scope. It used
 * to include the canonicals for an unfiltered run; the pre-#431 arithmetic, kept
 * on the grounds that "an unfiltered clean must keep announcing what it always
 * did". What it always did was over-report: merging a group deletes its
 * duplicates and *keeps* the canonical, so "Clean all (13)" over 8 broken URLs
 * and one group of 5 removed 12 profiles. The dialog's own second line already
 * said so ("5 duplicates merged into 1 canonical profile, which is kept"); only
 * the headline disagreed (PI-77).
 *
 * This must keep agreeing with `cleanPlanCounts(buildCleanPlan(...))`, which is
 * what the run actually executes; a property asserted in the tests, because the
 * two derive the same figures from different inputs and nothing else would catch
 * them drifting apart.
 */
export function cleanAllScope(
  filter: CleaningTypeFilter,
  brokenUrlProfiles: BrokenUrlProfile[],
  duplicateGroups: DuplicateGroup[],
): CleanScopeCounts {
  // A locked profile is out of every clean (#702), so out of its count too.
  const brokenUrlCount = brokenUrlProfiles.filter((p) => !p.is_cleaning_locked).length;
  const dupeProfileCount = duplicateProfileCount(duplicateGroups);
  const dupeGroups = duplicateGroups.filter(hasMergeable);
  const scoped = {
    broken_url: cleanScopeIncludes(filter, 'broken_url'),
    duplicate: cleanScopeIncludes(filter, 'duplicate'),
  };

  return {
    rowCount:
      (scoped.broken_url ? brokenUrlCount : 0) + (scoped.duplicate ? dupeProfileCount : 0),
    brokenUrlCount: scoped.broken_url ? brokenUrlCount : 0,
    dupeGroupCount: scoped.duplicate ? dupeGroups.length : 0,
    dupeProfileCount: scoped.duplicate ? dupeProfileCount : 0,
  };
}

/**
 * Split ids into batches no larger than `size`, preserving order.
 *
 * "Clean all" hands the endpoint every row on screen, and the endpoint caps a
 * request at `DELETE_DEAD_PROFILES_MAX_IDS`. A single oversized call would 400,
 * and only on the largest cleans, i.e. exactly the runs nobody exercises before
 * shipping.
 */
export function batchIds(ids: string[], size: number): string[][] {
  if (size <= 0) throw new RangeError('batchIds: size must be positive');
  const batches: string[][] = [];
  for (let i = 0; i < ids.length; i += size) batches.push(ids.slice(i, i + size));
  return batches;
}

/** Running totals of a batched broken-URL delete. */
export interface DeleteTotals {
  deleted_count: number;
  updated_pipelines: number;
  skipped_count: number;
}

/** Fold one batch's result into the running totals. */
export function addDeleteResult(totals: DeleteTotals, res: DeleteBrokenUrlsResult): DeleteTotals {
  return {
    deleted_count: totals.deleted_count + res.deleted_count,
    updated_pipelines: totals.updated_pipelines + res.updated_pipelines,
    skipped_count: totals.skipped_count + (res.skipped_count ?? 0),
  };
}

export const emptyDeleteTotals: DeleteTotals = {
  deleted_count: 0,
  updated_pipelines: 0,
  skipped_count: 0,
};

/** Whether a clean run under `filter` acts on rows of `type`. */
export function cleanScopeIncludes(filter: CleaningTypeFilter, type: CleaningRowType): boolean {
  return filter === 'all' || filter === type;
}

/** One part of the clean summary for a broken-URL profile deletion. */
export function deleteBrokenUrlsSummary(res: DeleteBrokenUrlsResult): string {
  return `${res.deleted_count} profile${res.deleted_count !== 1 ? 's' : ''} with a broken URL deleted`;
}

/**
 * Clean-summary parts for a broken-URL deletion. The scoped endpoint re-checks
 * every id it was given and reports the ones that had already been deleted or
 * re-enriched in between; worth saying, because the user picked those rows and
 * would otherwise read a smaller number than they ticked with no explanation.
 * The bulk endpoint never sets `skipped_count`, so its output is unchanged.
 */
export function deleteBrokenUrlsSummaryParts(res: DeleteBrokenUrlsResult): string[] {
  const parts = [deleteBrokenUrlsSummary(res)];
  if (res.skipped_count && res.skipped_count > 0) {
    parts.push(`${res.skipped_count} no longer eligible and skipped`);
  }
  return parts;
}

/**
 * Clean-summary parts for however many duplicates were merged (main part +
 * optional error part). Shared by the bulk merge-all and the per-canonical
 * scoped merge, which count their duplicates and their errors differently but
 * report them with the same words.
 */
export function mergedSummaryParts(merged: number, errorCount: number): string[] {
  const parts = [`${merged} duplicate${merged !== 1 ? 's' : ''} merged`];
  if (errorCount > 0) {
    parts.push(`${errorCount} error${errorCount > 1 ? 's' : ''}`);
  }
  return parts;
}

/**
 * One error snackbar for a whole scoped merge, however many batches failed. A
 * 30-row selection can fail 30 times; 30 snackbars are 30 dismissals for one
 * fact, so the count carries the scale and the first message carries the cause
 * (same shape as `mergeGroupNotice`'s error string).
 */
export function mergeErrorsNotice(errors: string[]): string[] {
  if (errors.length === 0) return [];
  return [`${errors.length} merge error${errors.length > 1 ? 's' : ''}: ${errors[0]}`];
}

/** Compose the final "Clean complete: …" snackbar from the collected parts. */
export function cleanSummary(parts: string[]): string | null {
  return parts.length > 0 ? `Clean complete: ${parts.join(', ')}` : null;
}

/** Single-group merge outcome → snackbar (success message or an error message). */
export interface MergeGroupNotice {
  success: string | null;
  error: string | null;
}

export function mergeGroupNotice(res: MergeGroupResult): MergeGroupNotice {
  if (res.errors && res.errors.length > 0) {
    return {
      success: null,
      error: `Merged ${res.merged} profiles, but ${res.errors.length} error${res.errors.length > 1 ? 's' : ''}: ${res.errors[0]}`,
    };
  }
  return {
    success: `Merged ${res.merged} duplicate${res.merged > 1 ? 's' : ''} into canonical profile`,
    error: null,
  };
}

// ── Scoped clean (only the ticked rows) ──

/** One merge call: the duplicates ticked inside a single group. */
export interface MergeBatch {
  canonicalId: string;
  duplicateIds: string[];
}

/**
 * What a scoped clean will actually do, derived from the ticked ids. The two
 * backends have different shapes (one call deletes every broken URL at once,
 * one call per canonical merges a group), so the split is decided here, as a
 * pure function, and the saga only executes it.
 */
export interface CleanPlan {
  /** Ticked broken-URL rows: deleted in a single call. */
  brokenUrlIds: string[];
  /** Ticked duplicates, grouped by the canonical they belong to. */
  mergeBatches: MergeBatch[];
  /** Ticked rows that are their group's canonical; kept, not merged, but counted. */
  canonicalIds: string[];
}

export interface CleanPlanCounts {
  /** Rows the clean will act on; the number the button shows. */
  actionable: number;
  brokenUrl: number;
  dupeProfiles: number;
  dupeGroups: number;
  canonicalKept: number;
}

/**
 * Walk the rows in table order (so both the batches and the ids inside them come
 * out row-ordered and deterministic) and sort the ticked ones into the plan. A
 * ticked canonical is the one row a clean must not touch: merging a group into
 * itself would delete the profile the group is being merged *towards*.
 */
export function buildCleanPlan(rows: CleaningRow[], selectedIds: ReadonlySet<string>): CleanPlan {
  const brokenUrlIds: string[] = [];
  const canonicalIds: string[] = [];
  const batches = new Map<string, string[]>();

  for (const row of rows) {
    if (!selectedIds.has(row.id)) continue;
    // Locked from cleaning (#702): never deleted nor merged away. The backend
    // refuses it anyway; leaving it out keeps the announced count honest. A
    // review-only row (#709) has no clean at all.
    if (!isCleaningRowSelectable(row)) continue;
    if (row.row_type === 'broken_url') {
      brokenUrlIds.push(row.id);
    } else if (row.is_canonical) {
      canonicalIds.push(row.id);
    } else if (row.canonical_id) {
      const batch = batches.get(row.canonical_id);
      if (batch) batch.push(row.id);
      else batches.set(row.canonical_id, [row.id]);
    }
    // A duplicate with no canonical to merge into cannot be acted on: dropped
    // rather than crashed, since the rows come from a backend payload.
  }

  return {
    brokenUrlIds,
    mergeBatches: [...batches].map(([canonicalId, duplicateIds]) => ({ canonicalId, duplicateIds })),
    canonicalIds,
  };
}

/** The plan's numbers, for the button label and the confirmation dialog. */
export function cleanPlanCounts(plan: CleanPlan): CleanPlanCounts {
  const dupeProfiles = plan.mergeBatches.reduce((acc, b) => acc + b.duplicateIds.length, 0);
  return {
    actionable: plan.brokenUrlIds.length + dupeProfiles,
    brokenUrl: plan.brokenUrlIds.length,
    dupeProfiles,
    dupeGroups: plan.mergeBatches.length,
    canonicalKept: plan.canonicalIds.length,
  };
}

/**
 * The dialog's extra bullet when the selection includes a canonical row. It is
 * why "3 selected" in the table can read `Clean selected (2)` on the button,
 * stated in words rather than left as an unexplained discrepancy.
 */
export function canonicalKeptNotice(count: number): string | null {
  if (count <= 0) return null;
  return count === 1
    ? '1 selected profile is the canonical of its group and will be kept, not merged'
    : `${count} selected profiles are the canonical of their group and will be kept, not merged`;
}
